import { StorageCheckoutData } from '@root/components/checkout/data';

type Args = {
  values: StorageCheckoutData;
  disposalEligible: boolean | undefined;
  /** Lead token from checkout values or client-data context. */
  leadToken?: string;
  storageEligible: boolean;
  loadingGeocode: boolean;
  /** True when the live geocode query failed (network). Do not treat as OSA. */
  geocodeUnavailable?: boolean;
};

/** Ordered challenger plan-page sections for gating, scroll, and validation. */
export type ChallengerFormSection =
  | 'storageLocation'
  | 'plan'
  | 'protection'
  | 'packing'
  | 'pickup';

const CHALLENGER_FORM_SECTION_ORDER: ChallengerFormSection[] = [
  'storageLocation',
  'plan',
  'protection',
  'packing',
  'pickup',
];

export type ChallengerSectionCompletion = Record<
  ChallengerFormSection,
  boolean
>;

export type ChallengerSectionEnabled = Record<ChallengerFormSection, boolean>;

/** Normalize checkout ZIP for comparisons (strip spaces, uppercase). */
export const normalizeCheckoutZip = (zip?: string): string =>
  (zip ?? '').replace(/\s+/g, '').toUpperCase();

/** Street + city + state + ZIP from address suggestions — required before calendar fetch. */
export const hasChallengerPickupAddress = (
  address: StorageCheckoutData['address'],
): boolean =>
  !!(
    address?.street &&
    String(address.street).trim() &&
    address?.city &&
    String(address.city).trim() &&
    address?.state &&
    String(address.state).trim() &&
    address?.zip &&
    String(address.zip).trim()
  );

/** Storage location step — validated, eligible ZIP (not loading geocode). */
const isChallengerZipStepComplete = ({
  values,
  storageEligible,
  loadingGeocode,
  geocodeUnavailable,
}: Args): boolean => {
  if (loadingGeocode) return false;
  if (!values.zipValidated) return false;
  if (!geocodeUnavailable && !storageEligible) return false;
  return true;
};

/** Plan size + commitment selected. */
export const isChallengerPlanStepComplete = (
  values: StorageCheckoutData,
): boolean => values.planSize !== undefined && values.commitment !== undefined;

/** Protection plan selected. */
export const isChallengerProtectionStepComplete = (
  values: StorageCheckoutData,
): boolean => values.protectionPlan !== undefined;

/** Packing bundle selected. */
export const isChallengerBundleStepComplete = (
  values: StorageCheckoutData,
): boolean => !!values.bundleKind;

/** Disposal answered when eligible; always complete when ineligible / unknown. */
export const isChallengerDisposalStepComplete = ({
  values,
  disposalEligible,
}: Pick<Args, 'values' | 'disposalEligible'>): boolean =>
  disposalEligible !== undefined &&
  (!disposalEligible || values.disposalSelected !== undefined);

/** Packing section (bundle + disposal when applicable). */
const isChallengerPackingStepComplete = (args: Args): boolean =>
  isChallengerBundleStepComplete(args.values) &&
  isChallengerDisposalStepComplete(args);

/** Pickup address + appointment window. */
const isChallengerPickupStepComplete = (values: StorageCheckoutData): boolean =>
  hasChallengerPickupAddress(values.address) && !!values.dateScheduled;

/** Per-section completion for sequential gating and scroll-to-field. */
export const getChallengerSectionCompletion = (
  args: Args,
): ChallengerSectionCompletion => ({
  storageLocation: isChallengerZipStepComplete(args),
  plan: isChallengerPlanStepComplete(args.values),
  protection: isChallengerProtectionStepComplete(args.values),
  packing: isChallengerPackingStepComplete(args),
  pickup: isChallengerPickupStepComplete(args.values),
});

/** Lower sections stay disabled until the section above is complete. */
export const getChallengerSectionEnabled = (
  completion: ChallengerSectionCompletion,
): ChallengerSectionEnabled => ({
  storageLocation: true,
  plan: completion.storageLocation,
  protection: completion.plan,
  packing: completion.protection,
  pickup: completion.packing,
});

/** First incomplete section in funnel order (for scroll on validation). */
export const getFirstIncompleteChallengerSection = (
  completion: ChallengerSectionCompletion,
): ChallengerFormSection | null =>
  CHALLENGER_FORM_SECTION_ORDER.find((section) => !completion[section]) ?? null;

/** User-facing copy when Continue is blocked or submit validation fails. */
export const CHALLENGER_FORM_INCOMPLETE_ERROR =
  'Complete all required fields above to continue.';

/**
 * Human-readable gaps that keep Continue disabled on challenger page 1.
 * Contact (name / email / phone) lives on Confirm — not required here.
 */
const challengerStoragePlanMissingSteps = ({
  values,
  disposalEligible,
  storageEligible,
  loadingGeocode,
  geocodeUnavailable,
}: Args): string[] => {
  const {
    zipValidated,
    planSize,
    commitment,
    protectionPlan,
    bundleKind,
    disposalSelected,
    address,
    dateScheduled,
  } = values;

  if (!zipValidated) {
    if (loadingGeocode) return ['Confirming your ZIP…'];
    if (!storageEligible) return ['Enter a storage-eligible ZIP code'];
  } else if (!geocodeUnavailable && !loadingGeocode && !storageEligible) {
    return ['Enter a storage-eligible ZIP code'];
  }

  const missing: string[] = [];
  if (!zipValidated) missing.push('Apply a valid ZIP under Storage location');
  if (planSize === undefined || commitment === undefined) {
    missing.push('Choose a storage plan and commitment');
  }
  if (protectionPlan === undefined) missing.push('Choose a protection plan');
  if (!bundleKind) {
    missing.push('Choose a packing option (I’ll pack myself or Full-service)');
  }
  if (disposalEligible && disposalSelected === undefined) {
    missing.push('Answer the disposal question');
  }
  if (!hasChallengerPickupAddress(address)) {
    missing.push(
      'Select a complete pickup address from the suggestions (street, city, state, ZIP)',
    );
  }
  if (!dateScheduled) {
    missing.push('Choose a pickup date and arrival window');
  }
  return missing;
};

/**
 * Challenger page-1 completion (Figma Desktop 03).
 * Contact (name / email / phone) lives on Confirm reservation — not required
 * to enable Continue. Lead create runs when contact is already present, or later
 * on confirm.
 */
export const isChallengerStoragePlanComplete = (args: Args): boolean =>
  challengerStoragePlanMissingSteps(args).length === 0;

/** Kept for callers that still care about an existing lead (e.g. draft sync). */
export const hasLeadToken = ({
  values,
  leadToken,
}: Pick<Args, 'values' | 'leadToken'>): boolean =>
  !!(values.leadToken || leadToken);

/** Confirm page contact fields (name / email / phone) — separate from pickup address. */
export const hasChallengerLeadInfo = (values: StorageCheckoutData): boolean =>
  !!(values.name?.trim() && values.email?.trim() && values.phone?.trim());
